'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { CheckCircle, ChevronDown, Clock, Hourglass } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'

export interface TimelineEvent {
  id: string | number
  title: string
  description?: string
  role?: string
  approver?: string
  isCompleted?: boolean
  isSkipped?: boolean
  timestamp?: string
  action?: string
  isCurrent?: boolean
}

interface VerticalEventTimelineProps {
  events: TimelineEvent[]
  onEventClick?: (event: TimelineEvent) => void
}

export function VerticalEventTimeline({ events, onEventClick }: VerticalEventTimelineProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0)

  const toggleExpand = (index: number) => {
    if (expandedIndex === index) {
      setExpandedIndex(null)
    }
    else {
      setExpandedIndex(null)
      const timer = setTimeout(() => {
        setExpandedIndex(index)
        clearTimeout(timer)
      }, 300)
    }
  }

  if (!events || events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Clock className="w-12 h-12 text-muted-foreground mb-4 opacity-50" />
        <p className="text-sm text-muted-foreground">No approval steps to display</p>
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 top-0 h-full w-0.5 bg-primary/20 z-0" />

      {events.map((item, index) => (
        (() => {
          const isSkipped = Boolean(item.isSkipped)
          const isCompleted = Boolean(item.isCompleted) && !isSkipped
          const isCurrent = Boolean(item.isCurrent) && !isSkipped

          return (
            <motion.div
              key={item.id}
              className={`mb-8 relative z-10 flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              {/* Timeline dot */}
              <div
                className={`absolute left-0 md:left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full z-10 flex items-center justify-center ${isSkipped
                  ? 'bg-amber-500'
                  : isCompleted
                    ? 'bg-green-500'
                    : isCurrent
                      ? 'bg-primary'
                      : 'bg-gray-300'
                }`}
              >
                {isSkipped || isCompleted
                  ? (
                    <CheckCircle className="w-4 h-4 text-white" />
                  )
                  : isCurrent
                    ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY, ease: 'linear' }}
                      >
                        <Hourglass className="w-4 h-4 text-white" />
                      </motion.div>
                    )
                    : <Clock className="w-4 h-4 text-white" />}
              </div>

              {/* Step number badge */}
              <div
                className={`md:w-1/2 flex ${index % 2 === 0 ? 'md:justify-end md:pr-8' : 'md:justify-start md:pl-8'
                }`}
              >
                <motion.div className="mb-4 md:mb-0" whileHover={{ scale: 1.05 }}>
                  <Badge
                    variant={isCompleted ? 'default' : isCurrent ? 'secondary' : 'outline'}
                    className={`text-sm py-1 px-3 flex items-center gap-1 ${isSkipped
                      ? 'bg-amber-100 border-amber-500 text-amber-700'
                      : isCompleted
                        ? 'bg-green-500 border-green-500 text-white'
                        : isCurrent
                          ? 'bg-primary/10 border-primary/50 text-primary font-semibold'
                          : 'bg-gray-100 border-gray-300 text-gray-500'
                    }`}
                  >
                    {isSkipped
                      ? (
                        <>
                          <Clock className="w-4 h-4" />
                          Step&nbsp;
                          {index + 1}
                        </>
                      )
                      : isCompleted
                        ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            Step&nbsp;
                            {index + 1}
                          </>
                        )
                        : isCurrent
                          ? (
                            <>
                              Step&nbsp;
                              {index + 1}
                            </>
                          )
                          : (
                            <>
                              <Clock className="w-4 h-4" />
                              Step&nbsp;
                              {index + 1}
                            </>
                          )}
                  </Badge>
                </motion.div>
              </div>

              {/* Card */}
              <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pl-8' : 'md:pr-8'}`}>
                <motion.div
                  layout
                  className="w-full"
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card
                    className={`overflow-hidden border-primary/10 shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer ${!isSkipped && !isCompleted && !isCurrent ? 'opacity-50' : ''
                    }`}
                    onClick={() => {
                      toggleExpand(index)
                      onEventClick?.(item)
                    }}
                  >
                    <CardContent className="p-0">
                      <div className="px-6 flex justify-between items-center">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-bold text-primary">
                              {item.title}
                            </h3>
                            {isCurrent && (
                              <Badge variant="default" className="text-xs">Current</Badge>
                            )}
                          </div>
                          {item.role && (
                            <p className="text-sm text-muted-foreground mt-1">
                              Role:
                              {' '}
                              {item.role}
                            </p>
                          )}
                          {item.approver && (
                            <p className="text-sm text-muted-foreground">
                              Approver:
                              {' '}
                              {item.approver}
                            </p>
                          )}
                          <div className="flex items-center text-sm text-muted-foreground mt-2 gap-2">
                            <CheckCircle
                              className={`w-4 h-4 ${isSkipped
                                ? 'text-amber-500'
                                : isCompleted
                                  ? 'text-green-500'
                                  : 'text-gray-400'
                              }`}
                            />
                            {isSkipped ? 'Skipped' : isCompleted ? 'Completed' : 'Pending'}
                          </div>
                        </div>
                        <motion.div
                          animate={{ rotate: expandedIndex === index ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        </motion.div>
                      </div>

                      <AnimatePresence>
                        {expandedIndex === index && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-6 pt-2 border-t border-border/50 space-y-3">
                              {item.action && (
                                <div>
                                  <p className="text-sm font-medium mb-1">Action</p>
                                  <p className="text-sm text-muted-foreground">{item.action}</p>
                                </div>
                              )}
                              {item.timestamp && (
                                <div>
                                  <p className="text-sm font-medium mb-1">Timestamp</p>
                                  <p className="text-sm text-muted-foreground">{item.timestamp}</p>
                                </div>
                              )}
                              {item.description && (
                                <div>
                                  <p className="text-sm font-medium mb-1">Comment</p>
                                  <p className="text-sm text-muted-foreground">{item.description}</p>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </CardContent>
                  </Card>
                </motion.div>
              </div>
            </motion.div>
          )
        })()
      ))}
    </div>
  )
}
